import { useEffect, useState, type KeyboardEvent } from 'react';
import { Modal } from '~/components/ui/Modal';
import { X, LogOut, Key, Image as ImageIcon } from 'lucide-react';
import { classNames } from '~/utils/classNames';
import { useLoaderData } from '@remix-run/react';
import { useTranslation } from '~/lib/hooks/useTranslation';
import { isDev, MAX_DIMENSION } from '~/utils/constants';
import { useFigmaAndAttachments, validateFigmaUrl } from '@/lib/hooks/useFigmaAndAttachments';

type AuthFrontendEnvs = {
  FIGMA_CLIENT_ID: string;
  FIGMA_REDIRECT_URI: string;
};

export default function figmaOAuthModal(): JSX.Element {
  const { t } = useTranslation();
  const env = useLoaderData<AuthFrontendEnvs>();

  const {
    tokenState,
    designState,
    uiState,
    hasFigmaDesign,
    hasFigmaError,
    hasValidToken,
    isExtracting,
    figmaImageBase64,
    initiateOAuth,
    disconnectFigma,
    extractDesign,
    detachDesign,
    cancelExtraction,
    closeModal,
    reset,
  } = useFigmaAndAttachments();

  // Reset state on open if requested via store
  useEffect(() => {
    if (uiState.isModalOpen && uiState.showResetState) {
      reset();
    }
  }, [uiState.isModalOpen, uiState.showResetState, reset]);

  // Local state for input
  const [localUrl, setLocalUrl] = useState(designState.figmaUrl);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [isTokenValid, setIsTokenValid] = useState(false);

  // Check token validity on mount and when modal opens
  useEffect(() => {
    if (uiState.isModalOpen) {
      hasValidToken().then(setIsTokenValid);
    }
  }, [uiState.isModalOpen, hasValidToken]);

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setLocalUrl(url);

    // Clear validation error when user starts typing
    if (urlError) {
      setUrlError(null);
    }

    // Only validate if the field has content and user has stopped typing
    if (url.trim()) {
      const validation = validateFigmaUrl(url);
      setUrlError(validation.error);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && localUrl.trim() && !isExtracting) {
      handleExtract();
    }
  };

  const handleExtract = () => {
    if (!localUrl.trim()) {
      return;
    }

    extractDesign(localUrl);
  };

  const handleConnect = () => {
    if (env.FIGMA_CLIENT_ID && env.FIGMA_REDIRECT_URI) {
      initiateOAuth(env.FIGMA_CLIENT_ID, env.FIGMA_REDIRECT_URI);
    } else {
      console.error('Missing Figma environment variables');
    }
  };

  const handleDetach = () => {
    detachDesign();
    setLocalUrl('');

    // onRemoveAttachedImage();
    // onExtractComplete({ image: null, fonts: [], colors: [] });
  };

  const handleClose = () => {
    cancelExtraction();
    closeModal();
  };

  // Helper for rendering colors/fonts
  const getStyleInfo = () => {
    const fonts = new Set(designState.design?.fonts || []);
    const colors = new Set(designState.design?.colors || []);
    return { fonts, colors };
  };

  const styleInfo = getStyleInfo();

  const renderContent = () => {
    if (!isTokenValid) {
      return (
        <div className="flex flex-col items-center gap-6 text-center">
          <div className="flex flex-col items-center gap-4 w-full">
            <div className="flex items-center border border-gray-600 rounded-lg px-4 pt-3 pb-1 m-2">
              <img src="/images/figma.svg" alt="Figma" className="w-20 h-20 mb-2" />
            </div>
            <div className="flex flex-col items-center gap-2">
              <h3 className="text-dualite-text-modal-primary text-base font-semibold m-0">
                Link your Figma account to Dualite.
              </h3>
              <p className="text-dualite-text-modal-secondary text-sm m-0">
                We'll open a secure login window in a new tab.
              </p>
            </div>
          </div>
          <button
            onClick={handleConnect}
            disabled={uiState.isValidatingToken}
            className="flex items-center justify-center gap-1.5 w-full px-2.5 py-2.5 bg-dualite-background-cta text-dualite-text-cta rounded-lg text-sm font-medium hover:bg-dualite-background-cta-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uiState.isValidatingToken ? (
              <div className="i-svg-spinners:90-ring-with-bg" />
            ) : (
              <>
                <Key size={16} />
                <span>{t('modals.figma.connectFigma')}</span>
              </>
            )}
          </button>
        </div>
      );
    }

    const imgUrl = isDev
      ? `https://proxy-worker.old-credit-270e.workers.dev/image?url=${tokenState.userImageUrl}`
      : tokenState.userImageUrl || '';

    if (!hasFigmaDesign && !hasFigmaError) {
      return (
        <div className="flex flex-col">
          <div className="mb-4 rounded-lg border border-dualite-border-secondary flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-dualite-primary-start flex items-center justify-center text-dualite-text-white font-medium overflow-hidden">
                {tokenState.userImageUrl ? (
                  <img
                    src={imgUrl}
                    className="w-full h-full object-cover rounded-full"
                    alt="Figma User"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  tokenState.userEmail?.split('@')[0].charAt(0).toUpperCase() || 'U'
                )}
              </div>
              <div className="flex flex-col">
                <span className="text-dualite-text-heading-start text-xs font-normal">Figma Account</span>
                <span className="text-dualite-text-heading-start font-medium text-sm truncate max-w-[200px] h-5">
                  {tokenState.userEmail || 'No email available'}
                </span>
              </div>
            </div>
            <button
              onClick={disconnectFigma}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-md text-red-400 text-sm transition-colors whitespace-nowrap"
            >
              <LogOut size={14} />
              Disconnect
            </button>
          </div>

          <div className="flex justify-between items-center mb-3">
            <label className="text-sm font-medium text-dualite-text-heading-start">
              {t('modals.figma.yourFigmaLink')}
            </label>
          </div>

          <input
            type="text"
            value={localUrl}
            onChange={handleUrlChange}
            onKeyDown={handleKeyDown}
            placeholder="https://www.figma.com/design/..."
            className={`w-full px-3.5 py-2.5 bg-dualite-background-button border rounded-lg text-dualite-text-modal-primary text-sm focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
              urlError ? 'border-red-500 focus:border-red-500' : 'border-dualite-border-primary focus:border-blue-500'
            }`}
            disabled={isExtracting}
          />

          {urlError ? (
            <p className="mt-2 text-xs text-red-400">{urlError}</p>
          ) : (
            <p className="mt-2 text-xs text-dualite-text-heading-start">{t('modals.figma.pasteUrl')}</p>
          )}

          <div className="mt-8 mb-4 p-4 bg-transparent border border-dualite-border-secondary rounded-lg">
            <h3 className="text-sm font-medium text-dualite-text-heading-start mb-2">
              {t('modals.figma.howToGetLink')}
            </h3>
            <ol className="list-decimal ml-5 space-y-2">
              <li className="text-xs text-dualite-text-heading-start">{t('modals.figma.step1')}</li>
              <li className="text-xs text-dualite-text-heading-start">{t('modals.figma.step2')}</li>
              <li className="text-xs text-dualite-text-heading-start">{t('modals.figma.step3')}</li>
              <li className="text-xs text-dualite-text-heading-start">{t('modals.figma.step4')}</li>
            </ol>
          </div>

          <button
            onClick={handleExtract}
            disabled={isExtracting || localUrl === '' || !!urlError}
            className="flex items-center justify-center gap-1.5 w-full mt-6 px-2.5 py-2.5 bg-dualite-background-cta hover:bg-dualite-background-ctaHover text-dualite-text-cta rounded-lg text-sm font-medium hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExtracting ? (
              <div className="i-svg-spinners:90-ring-with-bg" />
            ) : (
              <>
                <ImageIcon size={16} />
                <span>{t('modals.figma.import')}</span>
              </>
            )}
          </button>

          <button
            onClick={handleClose}
            className="flex items-center justify-center gap-1.5 w-full mt-3 px-2.5 py-2.5 bg-dualite-background-button hover:bg-dualite-background-buttonHover rounded-lg text-dualite-text-white text-sm font-medium transition-colors"
          >
            {isExtracting ? t('modals.figma.cancel') : t('modals.figma.close')}
          </button>
        </div>
      );
    }

    // Preview or Error state
    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 mt-6">
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="text-sm font-medium text-dualite-text-heading-start">Figma Design URL</label>
            </div>
            <input
              type="text"
              value={localUrl}
              onChange={handleUrlChange}
              onKeyDown={handleKeyDown}
              placeholder="https://www.figma.com/design/..."
              className={`w-full px-3.5 py-2.5 bg-dualite-background-tertiary border rounded-lg text-dualite-text-modal-primary text-sm focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed border-white/10 focus:border-blue-500`}
              disabled={true}
            />
          </div>

          <div>
            <div className="mb-3 rounded-lg border border-white/10 bg-dualite-background-tertiary/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-dualite-text-white font-medium overflow-hidden">
                  {tokenState.userImageUrl ? (
                    <img
                      src={imgUrl}
                      alt="Figma User"
                      className="w-full h-full object-cover rounded-full"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    tokenState.userEmail?.charAt(0).toUpperCase() || 'U'
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="text-dualite-text-heading-start text-xs font-normal">Figma Account</span>
                  <span className="text-dualite-text-heading-start font-medium text-sm truncate max-w-[120px] md:max-w-[180px]">
                    {tokenState.userEmail || 'No email available'}
                  </span>
                </div>
              </div>
              <button
                onClick={disconnectFigma}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-md text-red-400 text-sm transition-colors whitespace-nowrap"
              >
                <LogOut size={14} />
                Disconnect
              </button>
            </div>
          </div>

          <div className="md:col-span-2 lg:col-span-1">
            <h3 className="text-sm font-medium text-dualite-text-heading-start mb-2">Imported Design</h3>
            <div className="relative flex items-center justify-center rounded-lg overflow-hidden bg-dualite-background-tertiary min-h-[250px] max-h-[350px] mb-2.5">
              {figmaImageBase64 ? (
                <>
                  <img
                    src={figmaImageBase64}
                    alt="Imported design"
                    className="w-full h-auto max-h-[350px] object-contain"
                  />
                  {isExtracting && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="i-svg-spinners:90-ring-with-bg" />
                    </div>
                  )}
                </>
              ) : (
                <div className="text-gray-500">No image available</div>
              )}
              <button
                onClick={handleDetach}
                className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-black/70 rounded-md transition-colors flex text-dualite-text-modal-primary text-sm font-medium gap-1.5"
              >
                {t('modals.figma.detachDesign')}
                <X size={16} className="text-dualite-text-heading-start" />
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-4 max-h-[350px] overflow-y-auto md:col-span-2 lg:col-span-1">
            {styleInfo.colors.size > 0 && (
              <div>
                <h3 className="text-sm font-medium text-dualite-text-heading-start mb-4">
                  Colors ({styleInfo.colors.size})
                </h3>
                <div
                  className="flex flex-wrap gap-1.5 max-h-[100px] overflow-y-auto pr-2.5"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'var(--dualite-background-cta) var(--dualite-background-tertiary)',
                  }}
                >
                  {Array.from(styleInfo.colors).map((color) => (
                    <div
                      key={color}
                      className="flex items-center gap-1.5 px-3 py-2.5 rounded-md border border-dualite-border-primary"
                    >
                      <div className="w-3 h-3 rounded border border-white/10" style={{ backgroundColor: color }} />
                      <span className="text-xs text-dualite-text-gradient-end uppercase font-mono">{color}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {styleInfo.fonts.size > 0 && (
              <div>
                <h3 className="text-sm font-medium text-dualite-text-heading-start mb-4">
                  Fonts ({styleInfo.fonts.size})
                </h3>
                <div className="flex flex-wrap gap-1.5 max-h-[100px] overflow-y-auto pr-2.5 scrollbar-thin scrollbar-thumb-[var(--dualite-background-cta)] scrollbar-track-[var(--dualite-background-tertiary)]">
                  {Array.from(styleInfo.fonts).map((font) => (
                    <div key={font} className="px-3 py-1 rounded-md border border-dualite-border-primary">
                      <span className="text-xs text-dualite-text-gradient-end">{font}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {hasFigmaError ? (
          <div className="flex flex-col items-center text-center">
            <p className="text-sm text-red-500 bg-red-500/10 px-4 py-2.5 rounded-md max-w-[90%]">
              {designState.errorType === 'size'
                ? 'Design is too large to compress. Please select a smaller design.'
                : designState.errorType === 'dimension'
                  ? `Image dimensions exceed ${MAX_DIMENSION}px. Please select a smaller design.`
                  : `Design data is too large. Please select a smaller design.`}
            </p>
            <button
              onClick={handleDetach}
              className="flex items-center justify-center gap-1.5 px-5 py-2.5 mt-3 bg-red-500 hover:bg-red-600 rounded-lg text-dualite-text-white text-sm font-medium transition-colors"
            >
              Select New Design
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center">
            <button
              onClick={handleClose}
              disabled={isExtracting}
              className="flex items-center justify-center gap-1.5 px-10 py-2.5 bg-dualite-background-cta text-dualite-text-cta hover:bg-dualite-background-cta-hover rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue with this Design
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <Modal isOpen={uiState.isModalOpen} onClose={handleClose}>
      <div
        className={classNames(
          'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-51 w-full px-4',
          !figmaImageBase64 ? 'max-w-[480px]' : 'max-w-[900px]',
        )}
      >
        <div
          className={classNames(
            'bg-dualite-background-button border border-dualite-border-primary rounded-lg shadow-2xl relative w-full overflow-hidden',
            !figmaImageBase64 ? 'max-w-[480px]' : 'max-w-[900px]',
          )}
        >
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 p-2 text-dualite-text-modal-primary bg-transparent border-none rounded-md hover:bg-white/10 transition-colors z-10"
          >
            <X size={16} />
          </button>

          <div className="p-4">
            <div className="flex items-center gap-2 mb-3 pr-6">
              <h2 className="text-base font-semibold text-dualite-text-modal-primary">
                {isTokenValid ? t('modals.figma.importDesign') : t('modals.figma.connectAccount')}
              </h2>
              <button
                className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors px-2 py-1 rounded-full bg-white/10 hover:bg-white/20 cursor-pointer"
                onClick={() => window.open('https://alphadocs.dualite.dev/integrations/figma', '_blank')}
              >
                <span className="text-xs text-dualite-text-modal-secondary">Tutorial</span>
                <div className="i-ph:link w-3 h-3" />
              </button>
            </div>

            <div className="h-px bg-dualite-border-primary my-3 -mx-6" />

            {renderContent()}
          </div>
        </div>
      </div>
    </Modal>
  );
}
